// src/features/groups/hooks/useGroups.js
import { useState, useCallback, useMemo } from 'react';
import { 
  createGroup, 
  formatRelativeTime
} from '../services/groupService';
import { useGroupsContext } from '../contexts/GroupsContext';

/**
 * Hook for managing groups list and creation.
 * Uses inbox summary docs for instant loading without client-side joins.
 */
const useGroups = () => {
  const {
    items,
    hasServerSnapshot,
    error: subscriptionError,
    isOffline,
    isSignedIn,
  } = useGroupsContext();
  const groups = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );
  const [creating, setCreating] = useState(false);
  const [creationError, setCreationError] = useState(null);

  const hasSeenAnySnapshot = items !== null;

  let status;
  if (!isSignedIn) {
    status = 'empty';
  } else if (subscriptionError && groups.length === 0) {
    status = 'error';
  } else if (!hasSeenAnySnapshot) {
    status = isOffline ? 'offline' : 'loading';
  } else if (groups.length > 0) {
    status = 'ready';
  } else if (hasServerSnapshot) {
    status = 'empty';
  } else if (isOffline) {
    status = 'offline';
  } else {
    status = 'loading';
  }

  /**
   * Create a new group
   * @param {string} name - Group name
   * @param {File} iconFile - Group icon file
   * @param {Array} memberIds - Member IDs to add to group
   * @returns {Promise<Object>} Newly created group
   */
  const handleCreateGroup = useCallback(async (name, iconFile, memberIds) => {
    setCreating(true);
    setCreationError(null);
    
    try {
      const newGroup = await createGroup(name, iconFile, memberIds);
      
      // Groups list will update automatically via the subscription
      return newGroup;
    } catch (err) {
      console.error('Error creating group:', err);
      setCreationError(err.message || 'Failed to create group');
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);
  
  /**
   * Search groups by name
   * @param {string} searchTerm - Search term
   * @returns {Array} Filtered groups
   */
  const searchGroups = useCallback((searchTerm) => {
    if (!searchTerm?.trim()) return groups;
    
    const term = searchTerm.toLowerCase().trim();
    return groups.filter(group => 
      group.name.toLowerCase().includes(term)
    );
  }, [groups]);
  
  /**
   * Format relative time for display
   * @param {Date} date - Date to format
   * @returns {string} Formatted time string
   */
  const formatTime = useCallback((date) => {
    return formatRelativeTime(date);
  }, []);
  
  return {
    groups,
    status,
    loading: status === 'loading',
    isOffline,
    creating,
    loadError: subscriptionError,
    createError: creationError,
    createGroup: handleCreateGroup,
    searchGroups,
    formatTime
  };
};

export default useGroups; 
